import axios from 'axios';
import { baseURL } from '../Constants';

// Match Live State from webhook system
export interface MatchLiveState {
  match_id: string;
  get5_match_id: number | null;
  status: string;
  map_number: number;
  map_name: string | null;
  round_number: number;
  team1_id: number | null;
  team1_name: string | null;
  team1_score: number;
  team1_series_score: number | null;
  team1_side: string | null;
  team2_id: number | null;
  team2_name: string | null;
  team2_score: number;
  team2_series_score: number | null;
  team2_side: string | null;
  last_round_winner_side: string | null;
  last_round_reason: number | null;
  recent_kills: string | null;
  recent_rounds: string | null;
  updated_at: string;
}

// Round History
export interface RoundHistory {
  id: number;
  match_id: string;
  get5_match_id: number | null;
  map_number: number;
  round_number: number;
  round_time: number | null;
  winner_side: string | null;
  winner_team_id: number | null;
  team1_score: number;
  team2_score: number;
  reason: number | null;
  win_reason: string | null;
  created_at: string;
}

// Player Match Stats
export interface PlayerMatchStats {
  id: number;
  match_id: string;
  get5_match_id: number | null;
  map_number: number;
  steam_id: string;
  player_name: string;
  team_id: number | null;
  kills: number;
  deaths: number;
  assists: number;
  flash_assists: number;
  headshot_kills: number;
  damage: number;
  utility_damage: number;
  enemies_flashed: number;
  friendlies_flashed: number;
  rounds_played: number;
  bomb_plants: number;
  bomb_defuses: number;
  k1: number;
  k2: number;
  k3: number;
  k4: number;
  k5: number;
  v1: number;
  v2: number;
  v3: number;
  v4: number;
  v5: number;
  first_kills_t: number;
  first_kills_ct: number;
  first_deaths_t: number;
  first_deaths_ct: number;
  kast: number;
  mvp: number;
  score: number;
  updated_at: string;
}

// API Response wrappers
interface PlayerStatsResponse {
  players: PlayerMatchStats[];
  count: number;
}

/**
 * Get current live match state (fast query)
 */
export const getLiveMatchState = async (matchId: string, get5MatchId?: number): Promise<MatchLiveState | null> => {
  try {
    const params = get5MatchId !== undefined ? { get5MatchId } : {};
    const url = `${baseURL}/match/${matchId}/live-state`;
    console.log('[matchLiveService] Fetching live state from:', url, params);
    const response = await axios.get<MatchLiveState>(url, { params });
    console.log('[matchLiveService] Live state response:', response.data);
    return response.data; // Backend returns state directly, not wrapped
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log('[matchLiveService] No live state exists yet (404)');
      return null; // No live state exists yet
    }
    console.error('[matchLiveService] Error fetching live match state:', error);
    throw error;
  }
};

/**
 * Get round history for a match
 */
export const getRoundHistory = async (matchId: string, mapNumber?: number): Promise<RoundHistory[]> => {
  try {
    const params = mapNumber !== undefined ? { mapNumber } : {};
    const url = `${baseURL}/match/${matchId}/round-history`;
    console.log('[matchLiveService] Fetching round history from:', url, params);
    const response = await axios.get<RoundHistory[]>(url, { params });
    console.log('[matchLiveService] Round history response:', response.data);
    return response.data; // Backend returns array directly, not wrapped
  } catch (error) {
    console.error('[matchLiveService] Error fetching round history:', error);
    return [];
  }
};

/**
 * Get player stats for a match
 */
export const getPlayerStats = async (matchId: string, mapNumber?: number): Promise<PlayerMatchStats[]> => {
  try {
    const params = mapNumber !== undefined ? { mapNumber } : {};
    const url = `${baseURL}/match/${matchId}/player-stats`;
    console.log('[matchLiveService] Fetching player stats from:', url, params);
    const response = await axios.get<PlayerStatsResponse>(url, { params });
    console.log('[matchLiveService] Player stats response:', response.data);
    return response.data.players;
  } catch (error) {
    console.error('[matchLiveService] Error fetching player stats:', error);
    return [];
  }
};

/**
 * Clear test data for a match (requires password)
 */
export const clearMatchTestData = async (matchId: string, password: string): Promise<any> => {
  try {
    const response = await axios.post(`${baseURL}/match/test/clear-match-data`, {
      matchId,
      password
    });
    return response.data;
  } catch (error) {
    console.error('Error clearing match test data:', error);
    throw error;
  }
};

/**
 * Calculate player rating (HLTV Rating 1.0 - Full Formula)
 * Uses multi-kill data for more accurate ratings
 * https://www.hltv.org/news/20695/introducing-rating-10
 */
export const calculatePlayerRating = (stats: PlayerMatchStats): number => {
  console.log('[Rating 1.0] Input stats:', {
    player: stats.player_name,
    kills: stats.kills,
    deaths: stats.deaths,
    rounds: stats.rounds_played,
    k2: stats.k2,
    k3: stats.k3,
    k4: stats.k4,
    k5: stats.k5
  });

  if (stats.rounds_played === 0) return 0;

  // Kill Rating
  const killsPerRound = stats.kills / stats.rounds_played;
  const killRating = killsPerRound / 0.679;
  console.log('[Rating 1.0] Kill Rating:', { killsPerRound, killRating });

  // Survival Rating
  const survivalRate = (stats.rounds_played - stats.deaths) / stats.rounds_played;
  const adjustedSurvivalRating = survivalRate / 0.317;
  console.log('[Rating 1.0] Survival Rating:', { survivalRate, adjustedSurvivalRating });

  // Multi-Kill Rating (weighted: 2k=1, 3k=4, 4k=9, 5k=16)
  const multiKillRounds = (stats.k2 * 1 + stats.k3 * 4 + stats.k4 * 9 + stats.k5 * 16) / stats.rounds_played;
  const adjustedMultiKillRating = multiKillRounds / 1.277;
  console.log('[Rating 1.0] Multi-Kill Rating:', { multiKillRounds, adjustedMultiKillRating });

  // Final Rating 1.0
  const rating = (killRating + (0.7 * adjustedSurvivalRating) + adjustedMultiKillRating) / 2.7;
  const finalRating = Math.max(0, Math.min(3, rating));
  console.log('[Rating 1.0] Final:', { rawRating: rating, clampedRating: finalRating });

  return finalRating;
};

/**
 * Calculate ADR (Average Damage per Round)
 */
export const calculateADR = (stats: PlayerMatchStats): number => {
  if (stats.rounds_played === 0) return 0;
  return Math.round(stats.damage / stats.rounds_played);
};

/**
 * Calculate K/D ratio
 */
export const calculateKD = (stats: PlayerMatchStats): number => {
  if (stats.deaths === 0) return stats.kills;
  return stats.kills / stats.deaths;
};

/**
 * Calculate KAST percentage
 */
export const calculateKASTPercent = (stats: PlayerMatchStats): number => {
  if (stats.rounds_played === 0) return 0;
  return Math.round((stats.kast / stats.rounds_played) * 100);
};

/**
 * Get win reason text (CS2 round end reasons)
 */
export const getWinReasonText = (reason: number | null): string => {
  if (reason === null) return 'Unknown';

  const reasons: { [key: number]: string } = {
    0x0: 'Unknown',
    0x1: 'Target Bombed',
    0x4: 'Terrorists Escaped',
    0x5: 'CTs Prevent Escape',
    0x6: 'Escaping Terrorists Neutralized',
    0x7: 'Bomb Defused',
    0x8: 'CTs Win',
    0x9: 'Terrorists Win',
    0xA: 'Round Draw',
    0xB: 'All Hostages Rescued',
    0xC: 'Target Saved',
    0xD: 'Hostages Not Rescued',
    0xE: 'Terrorists Not Escaped',
    0x10: 'Game Commencing',
    0x11: 'Terrorists Surrender',
    0x12: 'CTs Surrender',
    0x13: 'Bomb Planted',
    0x14: 'CTs Reached Hostage',
    0x15: 'Survival Win',
    0x16: 'Survival Draw'
  };

  return reasons[reason] || 'Unknown';
};
